import { useMemo, useState } from 'react';
import { GetStaticComponentProps, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { debounce } from 'lodash-es';

import { isColorBright, resolveColorProperty } from '@gn-audio/jss-components';

import { executeGQLQuery } from 'api/graphqlApi';
import {
  cableGuideEntries,
  cableOrExtenderQuery,
  hdmiCableOrExtenderQuery,
  hubOrDockQuery,
  wirelessConferencingQuery,
} from 'composables/CableGuide/cableGuideQueries';
import {
  ActiveTab,
  BaseItem,
  CableGuideEntries,
  CableGuideProps,
  CableItem,
  CatalogQueryResult,
  DockItem,
  HdmiCableItem,
  WirelessItem,
} from 'composables/CableGuide/cableGuideTypes';

const CATEGORY_QUERY_MAP: Record<string, string> = {
  'Cables And Extenders': cableOrExtenderQuery,
  Docks: hubOrDockQuery,
  'HDMI Cables and Extenders': hdmiCableOrExtenderQuery,
  'Wireless Conferencing': wirelessConferencingQuery,
};

function fieldValuesOf(item: BaseItem): string[] {
  return Object.values(item)
    .filter((v): v is { value: string } => v != null && typeof v === 'object' && 'value' in v)
    .map((v) => v.value);
}

const ALL_PRODUCTS = 'All';

function filterItems<T extends BaseItem>(
  items: T[],
  selectedProduct: string,
  searchTerm: string
): T[] {
  return items.filter((item) => {
    if (
      selectedProduct !== ALL_PRODUCTS &&
      !item.compatibleProduct.targetItems.some((product) => product.name === selectedProduct)
    ) {
      return false;
    }
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      const allValues = [
        ...fieldValuesOf(item),
        ...item.region.targetItems.map((region) => region.name),
        ...item.compatibleProduct.targetItems.map((product) => product.name),
      ];
      if (!allValues.some((value) => value?.toLowerCase().includes(lower))) return false;
    }
    return true;
  });
}

const Cell = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col">
    <span className="font-bold">{label}</span>
    <span className="whitespace-pre-line">{value || 'N/A'}</span>
  </div>
);

function isSafeUrl(url: string): boolean {
  if (url.startsWith('/')) {
    return true;
  }
  try {
    const { protocol } = new URL(url);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

const LinkCell = ({ url, label }: { url: string; label: string }) => {
  const safeUrl = isSafeUrl(url) ? url : null;
  return (
    <div className="flex flex-col">
      <span className="font-bold">Link</span>
      {safeUrl ? (
        <a href={safeUrl} className="underline" target="_blank" rel="noreferrer">
          {label}
        </a>
      ) : (
        <span>N/A</span>
      )}
    </div>
  );
};

const cardClass =
  'border-slate-200 grid grid-cols-1 gap-4 border-b px-4 py-4 text-left sm:grid-cols-2 md:gap-8 md:px-8 md:py-8 xl:grid-cols-4';

const CableCard = ({ item, productPageLabel }: { item: CableItem; productPageLabel: string }) => (
  <div className={cardClass}>
    <Cell label="Product Type" value={item.productType.value} />
    <Cell label="Product Name" value={item.productName.value} />
    <Cell label="Brand" value={item.brand.value} />
    <Cell label="USB Specification" value={item.uSBSpecification.value} />
    <Cell label="Length" value={item.length.value} />
    <Cell label="USB Connection Type" value={item.uSBConnectionType.value} />
    <LinkCell url={item.link.url} label={productPageLabel} />
    <Cell label="Disclaimer" value={item.disclaimer.value} />
    <Cell label="Regions" value={item.region.targetItems.map((r) => r.name).join(', ')} />
    <Cell
      label="Compatible Products"
      value={item.compatibleProduct.targetItems.map((p) => p.name).join(', ')}
    />
  </div>
);

const DockCard = ({ item }: { item: DockItem }) => (
  <div className={cardClass}>
    <Cell label="Product Type" value={item.productType.value} />
    <Cell label="Product Name" value={item.productName.value} />
    <Cell label="Brand" value={item.brand.value} />
    <Cell label="Host Interface" value={item.hostInterface.value} />
    <Cell label="Video Ports" value={item.videoPorts.value} />
    <Cell label="Max Displays" value={item.maxDisplays.value} />
    <Cell label="USB Ports" value={item.uSBPorts.value} />
    <Cell label="Disclaimer" value={item.disclaimer.value} />
    <Cell label="Regions" value={item.region.targetItems.map((r) => r.name).join(', ')} />
    <Cell
      label="Compatible Products"
      value={item.compatibleProduct.targetItems.map((p) => p.name).join(', ')}
    />
  </div>
);

const HdmiCard = ({
  item,
  productPageLabel,
}: {
  item: HdmiCableItem;
  productPageLabel: string;
}) => (
  <div className={cardClass}>
    <Cell label="Product Type" value={item.productType.value} />
    <Cell label="Product Name" value={item.productName.value} />
    <Cell label="Brand" value={item.brand.value} />
    <Cell label="HDMI Specification" value={item.hDMISpecification.value} />
    <Cell label="Length" value={item.length.value} />
    <Cell label="Compartment Cover Compatible" value={item.compartmentCoverCompatbile.value} />
    <LinkCell url={item.link.url} label={productPageLabel} />
    <Cell label="Disclaimer" value={item.disclaimer.value} />
    <Cell label="Regions" value={item.region.targetItems.map((r) => r.name).join(', ')} />
    <Cell
      label="Compatible Products"
      value={item.compatibleProduct.targetItems.map((p) => p.name).join(', ')}
    />
  </div>
);

const WirelessCard = ({ item }: { item: WirelessItem }) => (
  <div className={cardClass}>
    <Cell label="Product Type" value={item.productType.value} />
    <Cell label="Product Name" value={item.productName.value} />
    <Cell label="Brand" value={item.brand.value} />
    <Cell label="Product Firmware" value={item.productFirmware.value} />
    <Cell label="Product Software Version" value={item.productSoftwareVersion.value} />
    <Cell label="Jabra Firmware" value={item.jabraFirmware.value} />
    <Cell label="Disclaimer" value={item.disclaimer.value} />
    <Cell label="Regions" value={item.region.targetItems.map((r) => r.name).join(', ')} />
    <Cell
      label="Compatible Products"
      value={item.compatibleProduct.targetItems.map((p) => p.name).join(', ')}
    />
  </div>
);

function TabPanel<T extends BaseItem>({
  tabId,
  activeTab,
  items,
  noResultsLabel,
  renderItem,
}: {
  tabId: ActiveTab;
  activeTab: ActiveTab;
  items: T[];
  noResultsLabel: string;
  renderItem: (item: T) => React.ReactNode;
}) {
  return (
    <div
      id={`panel-${tabId}`}
      role="tabpanel"
      aria-labelledby={`tab-${tabId}`}
      hidden={activeTab !== tabId}
    >
      {activeTab === tabId &&
        (items.length === 0 ? (
          <p className="text-slate-500 p-4 md:p-8">{noResultsLabel}</p>
        ) : (
          <>{items.map(renderItem)}</>
        ))}
    </div>
  );
}

const CableGuide = (props: CableGuideProps) => {
  const { cablesAndExtenders, docks, hdmiCables, wirelessConferencing, fields } = props;

  const allLabel = fields.all?.value;
  const productPageLabel = fields.productPage?.value;
  const filterByProductLabel = fields.filterByProduct?.value;
  const noResultsLabel = fields.noResults?.value;
  const headlineLabel = fields.headline?.value;
  const descriptionLabel = fields.description?.value;
  const searchLabel = fields.search?.value;
  const resultsLabel = fields.results?.value;

  const backgroundColorHex = resolveColorProperty(fields.backgroundColor?.value ?? '#ffffff');
  const isBgBright = isColorBright(backgroundColorHex);

  const cableResults = useMemo(
    () => cablesAndExtenders?.children.results ?? [],
    [cablesAndExtenders]
  );
  const dockResults = useMemo(() => docks?.children.results ?? [], [docks]);
  const hdmiResults = useMemo(() => hdmiCables?.children.results ?? [], [hdmiCables]);
  const wirelessResults = useMemo(
    () => wirelessConferencing?.children.results ?? [],
    [wirelessConferencing]
  );

  const [activeTab, setActiveTab] = useState<ActiveTab>('cables');
  const [selectedProduct, setSelectedProduct] = useState(ALL_PRODUCTS);
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const debouncedSetSearchTerm = useMemo(() => debounce(setSearchTerm, 300), []);

  const allProductNames = useMemo(
    () =>
      Array.from(
        new Set([
          ...cableResults.flatMap((item) =>
            item.compatibleProduct.targetItems.map((product) => product.name)
          ),
          ...dockResults.flatMap((item) =>
            item.compatibleProduct.targetItems.map((product) => product.name)
          ),
          ...hdmiResults.flatMap((item) =>
            item.compatibleProduct.targetItems.map((product) => product.name)
          ),
          ...wirelessResults.flatMap((item) =>
            item.compatibleProduct.targetItems.map((product) => product.name)
          ),
        ])
      ).sort(),
    [cableResults, dockResults, hdmiResults, wirelessResults]
  );

  const filteredCables = useMemo(
    () => filterItems(cableResults, selectedProduct, searchTerm),
    [cableResults, selectedProduct, searchTerm]
  );
  const filteredDocks = useMemo(
    () => filterItems(dockResults, selectedProduct, searchTerm),
    [dockResults, selectedProduct, searchTerm]
  );
  const filteredHdmi = useMemo(
    () => filterItems(hdmiResults, selectedProduct, searchTerm),
    [hdmiResults, selectedProduct, searchTerm]
  );
  const filteredWireless = useMemo(
    () => filterItems(wirelessResults, selectedProduct, searchTerm),
    [wirelessResults, selectedProduct, searchTerm]
  );

  const totalVisible =
    filteredCables.length + filteredDocks.length + filteredHdmi.length + filteredWireless.length;

  const tabs: { id: ActiveTab; label: string; count: number }[] = [
    { id: 'cables', label: fields.cablesExtenders?.value, count: filteredCables.length },
    { id: 'docks', label: fields.hubsDocks?.value, count: filteredDocks.length },
    { id: 'hdmi', label: fields.hdmiCablesExtenders?.value, count: filteredHdmi.length },
    { id: 'wireless', label: fields.wirelessConferencing?.value, count: filteredWireless.length },
  ];

  return (
    <div
      className="container-fluid"
      {...(fields?.backgroundColor?.value && {
        style: {
          background: backgroundColorHex,
          color: isBgBright ? 'black' : 'white',
        },
      })}
    >
      <div className="container">
        <div className="mb-12">
          <h3 className="mb-2 text-2xl font-bold">{headlineLabel}</h3>
          <p>{descriptionLabel}</p>
        </div>

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
          <label htmlFor="cable-guide-product-filter" className="sr-only">
            {filterByProductLabel}
          </label>
          <select
            id="cable-guide-product-filter"
            value={selectedProduct}
            onChange={(e) => setSelectedProduct(e.target.value)}
            className="border-slate-400 h-[45px] w-full rounded-lg border bg-white pl-4 pr-3 sm:w-auto"
          >
            <option value="All">{allLabel}</option>
            {allProductNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          <input
            type="search"
            placeholder={searchLabel}
            aria-label={searchLabel}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              debouncedSetSearchTerm(e.target.value);
            }}
            className="border-slate-400 h-[45px] w-full rounded-lg border bg-white pl-4 pr-3 sm:w-auto"
          />

          <span>
            <em className="font-bold not-italic">{totalVisible}</em> {resultsLabel}
          </span>
        </div>

        <div className="-mb-px flex gap-2 overflow-x-auto" role="tablist">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                id={`tab-${tab.id}`}
                role="tab"
                aria-selected={isActive}
                aria-controls={`panel-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={classNames(
                  'flex-shrink-0 rounded-t-lg border px-3 py-3 text-sm focus:outline-none md:flex-1 md:px-4 md:py-4 md:text-base',
                  isActive
                    ? 'border-slate-300 border-b-white bg-white font-semibold'
                    : 'border-transparent bg-[#d2dfd9] hover:bg-[#ecf1ef]'
                )}
              >
                {tab.label} (<span className="font-semibold">{tab.count}</span>)
              </button>
            );
          })}
        </div>

        <div className="border-slate-300 border bg-white">
          <TabPanel
            tabId="cables"
            activeTab={activeTab}
            items={filteredCables}
            noResultsLabel={noResultsLabel}
            renderItem={(item) => (
              <CableCard key={item.id} item={item} productPageLabel={productPageLabel} />
            )}
          />

          <TabPanel
            tabId="docks"
            activeTab={activeTab}
            items={filteredDocks}
            noResultsLabel={noResultsLabel}
            renderItem={(item) => <DockCard key={item.id} item={item} />}
          />

          <TabPanel
            tabId="hdmi"
            activeTab={activeTab}
            items={filteredHdmi}
            noResultsLabel={noResultsLabel}
            renderItem={(item) => (
              <HdmiCard key={item.id} item={item} productPageLabel={productPageLabel} />
            )}
          />

          <TabPanel
            tabId="wireless"
            activeTab={activeTab}
            items={filteredWireless}
            noResultsLabel={noResultsLabel}
            renderItem={(item) => <WirelessCard key={item.id} item={item} />}
          />
        </div>
      </div>
    </div>
  );
};

export const getStaticProps: GetStaticComponentProps = async (rendering) => {
  try {
    const entries = await executeGQLQuery<CableGuideEntries>(cableGuideEntries, {
      path: rendering.dataSource,
      language: 'en',
    });

    const categoryPaths = entries.item.children.results;

    const resultsByName: Record<string, CatalogQueryResult<BaseItem>['item'] | null> = {};

    await Promise.all(
      categoryPaths.map(async ({ path }) => {
        const folderName = path.split('/').pop() ?? '';
        const query = CATEGORY_QUERY_MAP[folderName];

        if (!query) {
          resultsByName[folderName] = null;
          return;
        }

        const result = await executeGQLQuery<CatalogQueryResult<BaseItem>>(query, {
          path,
          language: 'en',
          first: 100,
        });
        resultsByName[folderName] = result.item;
      })
    );

    const cablesAndExtenders = resultsByName['Cables And Extenders'] ?? null;
    const docks = resultsByName['Docks'] ?? null;
    const hdmiCables = resultsByName['HDMI Cables and Extenders'] ?? null;
    const wirelessConferencing = resultsByName['Wireless Conferencing'] ?? null;

    return { cablesAndExtenders, docks, hdmiCables, wirelessConferencing };
  } catch (error) {
    console.error('[CableGuide] getStaticProps failed:', error);
    return { cablesAndExtenders: null, docks: null, hdmiCables: null, wirelessConferencing: null };
  }
};

export default withDatasourceCheck()<CableGuideProps>(CableGuide);
